import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import parametersToJsonSchema from '../../../lib/api-explorer/parameters-to-json-schema';
import cloneDeep from 'hs-lodash/cloneDeep';
import isEmpty from 'hs-lodash/isEmpty';
import isNil from 'hs-lodash/isNil';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H5 from 'UIComponents/elements/headings/H5';
import AccordionTransition from 'UIComponents/transitions/AccordionTransition';
import ParamForm from '../../ParamForm/components/ParamForm';
// @ts-expect-error No types given for file
import LazyLoadWrapper from '../../Response/components/LazyLoadWrapper';
import { useDebounce } from '../hooks/useDebounce';
// @ts-expect-error No types given for file
import PropertyCollapser from '../utils/PropertyCollapser';
import { Text } from 'foundations-components/components/Text';
import { ExpandableSection } from 'foundations-components/transitional/components/ExpandableSection';
import { Flex } from 'foundations-components/components/Flex';
import { RightIcon } from 'foundations-assets';
import { m } from 'framer-motion';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MAX_PARAMS = 3;
/**
 * this is the same expandable caret used by the ExpandableSection component with the exception of the arrow being animated to face up (270 degrees0 instead of right (0 degrees)
 * the source for the original can be found here: https://git.hubteam.com/HubSpot/foundations-components/blob/58535e4459939097b59e6ac24344365c6fe74cb0/foundations-components/static-1.4970/js/transitional/components/ExpandableSection/ExpandableSection.tsx
 */
const ExpandableCaret = ({
  open
}) => {
  return /*#__PURE__*/_jsx(m.div, {
    role: "img",
    style: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexBasis: '44px',
      flexShrink: 0
    },
    animate: {
      rotate: open ? -90 : 0
    },
    children: /*#__PURE__*/_jsx(RightIcon, {
      color: "icon-interactive-hover"
    })
  });
};
const ParamsWrapper = styled.div.withConfig({
  displayName: "Params__ParamsWrapper",
  componentId: "sc-14mrio9-0"
})(["background-color:", ";"], ({
  theme
}) => theme.color['fill-surface-default']);
const CaretIcon = ({
  open
}) => /*#__PURE__*/_jsx(Flex, {
  justifyContent: "center",
  flexShrink: 0,
  paddingInlineStart: "400",
  paddingInlineEnd: "400",
  children: /*#__PURE__*/_jsx(ExpandableCaret, {
    open: open
  })
});
const Params = ({
  formData,
  oas,
  operation,
  onChange,
  usageTracker
}) => {
  const [collapser] = useState(new PropertyCollapser(MAX_PARAMS));
  const [fullSchema, setFullSchema] = useState(null);
  const [state, setState] = useState({
    initialParamsJsonSchema: null,
    expandedParamsJsonSchema: null,
    isCalculated: false
  });
  const [isOpen, setIsOpen] = useState(false);

  // use a debounce on the is open to prevent text flash on page as soon as button is clicked while params are still expanding
  const debouncedIsOpen = useDebounce(isOpen, 200);

  /**
   * calculates both initial schema and expanded schema
   */
  const calculateSchemas = useCallback(() => {
    const full = fullSchema || parametersToJsonSchema(operation, oas);
    setFullSchema(full);
    const collapsedParams = collapser.collapsedSchema || collapser.calculateCollapsedSchema(cloneDeep(full), formData, operation);
    const additionalParameters = collapser.expandedSchema || collapser.calculateExpandedSchema(cloneDeep(full), formData, operation);
    setState({
      initialParamsJsonSchema: collapsedParams,
      expandedParamsJsonSchema: (additionalParameters || []).filter(schema => !isNil(schema.schema) && !isEmpty(schema.schema && schema.schema.properties)),
      isCalculated: true
    });
  }, [collapser, formData, fullSchema, oas, operation]);
  useEffect(() => {
    calculateSchemas();
  }, [calculateSchemas]);
  const renderContent = () => {
    const {
      initialParamsJsonSchema,
      expandedParamsJsonSchema,
      isCalculated
    } = state;
    if ((!initialParamsJsonSchema || !initialParamsJsonSchema.length) && isCalculated) {
      return /*#__PURE__*/_jsx(Text, {
        fontStyle: "italic",
        paddingBlockEnd: "600",
        paddingInlineStart: "600",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "developer-docs-shared-components.params.body.noParams"
        })
      });
    }
    const wrapperId = `wrapper-${operation.operationId}`;
    return /*#__PURE__*/_jsx(LazyLoadWrapper, {
      mountOnce: true,
      children: /*#__PURE__*/_jsxs("div", {
        id: wrapperId,
        children: [isCalculated && /*#__PURE__*/_jsx("div", {
          className: "params-block",
          children: (initialParamsJsonSchema || []).map(schema => /*#__PURE__*/_jsx(ParamForm
          /*  @ts-expect-error Sep-25-2024, 17:26UTC FixMe: Complete Migration to TypeScript  */, {
            formData: formData[schema.type],
            id: `form-${operation.operationId}`,
            idPrefix: operation.operationId,
            onChange: form => onChange({
              [schema.type]: form.formData
            }),
            schema: schema.schema,
            schemaGroup: schema.label,
            usageTracker: usageTracker
          }, `${schema.type}-form`))
        }), expandedParamsJsonSchema && expandedParamsJsonSchema.length > 0 && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(AccordionTransition, {
            open: isOpen,
            children: /*#__PURE__*/_jsx("div", {
              className: "params-block",
              children: (expandedParamsJsonSchema || []).map(schema => {
                return /*#__PURE__*/_jsx(ParamForm
                /*  @ts-expect-error Sep-25-2024, 17:26UTC FixMe: Complete Migration to TypeScript  */, {
                  formData: formData[schema.type],
                  id: `form-${operation.operationId}`,
                  idPrefix: operation.operationId,
                  onChange: form => onChange({
                    [schema.type]: form.formData
                  }),
                  schema: schema.schema,
                  schemaGroup: schema.label,
                  usageTracker: usageTracker
                }, `${schema.type}-form`);
              })
            })
          }), /*#__PURE__*/_jsx(ExpandableSection, {
            titleText: debouncedIsOpen ? /*#__PURE__*/_jsx(FormattedMessage, {
              message: "developer-docs-shared-components.params.body.newCollapseButton"
            }) : /*#__PURE__*/_jsx(FormattedMessage, {
              message: "developer-docs-shared-components.params.body.newExpandButton"
            }),
            indentation: "none",
            onOpenChange: () => {
              if (isOpen) {
                setIsOpen(false);
                const wrapperElement = document.getElementById(wrapperId);
                if (wrapperElement) {
                  wrapperElement.scrollIntoView();
                }
              } else {
                setIsOpen(true);
              }
            },
            open: isOpen,
            Caret: CaretIcon
          })]
        })]
      })
    });
  };
  return /*#__PURE__*/_jsxs(ParamsWrapper, {
    className: "params",
    children: [/*#__PURE__*/_jsx(H5, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "developer-docs-shared-components.params.header.parameters"
      })
    }), renderContent()]
  });
};
export default Params;